import {
  Component,
  computed,
  inject,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import type { FullCandidate } from '@core/candidate-types';
import { EvaluateCandidateButtonComponent } from '@core/candidate-ui';
import { FeatureGuardFacade, GuardedFeature } from '@core/feature-guard-legacy';
import { EditQuickEvaluationDropdownComponent } from '@core/interview-results-legacy';
import { AppNavigatorService } from '@core/navigation-legacy';
import { OfferSelectOptionHelper } from '@core/offers-ui';
import type { Placement } from '@core/placements-types';
import { EvaluationsAnalytics, WorkflowTemplatesAnalytics } from '@core/temp-legacy';
import { DropdownWidth } from '@core/user-interface-legacy';
import { WalkthroughStepOriginDirective } from '@core/walkthrough-ui';
import { WorkflowTemplatesFeatures } from '@core/workflow-templates-types';
import type { AddEvaluationSubmit } from '@recruitee/evaluations-types';

import { AddEvaluationDropdownComponent } from '../../dropdowns/add-evaluation-dropdown/add-evaluation-dropdown.component';

@Component({
  selector: 'rt-evaluate-candidate-container',
  templateUrl: './evaluate-candidate-container.component.html',
  imports: [
    EvaluateCandidateButtonComponent,
    EditQuickEvaluationDropdownComponent,
    AddEvaluationDropdownComponent,
    WalkthroughStepOriginDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [WorkflowTemplatesAnalytics, EvaluationsAnalytics],
})
export class EvaluateCandidateContainerComponent {
  private readonly appNavigator = inject(AppNavigatorService);
  private readonly analyticsWorkflowTemplates = inject(WorkflowTemplatesAnalytics);
  private readonly evaluationsAnalytics = inject(EvaluationsAnalytics);
  private readonly featureGuardFacade = inject(FeatureGuardFacade);
  private readonly evaluateButton = viewChild(EvaluateCandidateButtonComponent);
  private readonly editDropdown = viewChild(EditQuickEvaluationDropdownComponent);

  protected readonly candidate = input.required<FullCandidate>();
  protected readonly availablePlacements = input.required<Placement[]>();
  protected readonly offers = computed(() =>
    OfferSelectOptionHelper.placementsToOfferSelectOptions(this.availablePlacements()),
  );
  protected readonly nestedDropdownParent = computed(() => this.editDropdown()?.rtDropdown());
  protected readonly DropdownWidth = DropdownWidth;
  protected readonly isEvaluationFormsEntitled = toSignal(
    this.featureGuardFacade.checkFeatureAvailability(GuardedFeature.EvaluationForms),
    { initialValue: true },
  );

  public readonly openInEvaluationTab = output<void>();

  protected onAddEvaluation(data: AddEvaluationSubmit): void {
    this.closeOverlay();

    this.analyticsWorkflowTemplates.trackTemplateInsertion(
      WorkflowTemplatesFeatures.EvaluationForms,
    );
    this.evaluationsAnalytics.trackRequestEvaluation();

    this.appNavigator.openCandidateDialog(this.candidate().id, {
      openInterview: true,
      interviewOfferId: data.offerId,
      interviewTalentPoolId: data.talentPoolId,
      interviewTemplateId: data.templateId,
    });
  }

  protected onAddQuickEvaluation(): void {
    this.closeOverlay();

    this.appNavigator.openCandidateDialog(this.candidate().id, {
      openInterview: true,
      quickEvaluation: true,
    });
  }

  protected closeOverlay(): void {
    this.evaluateButton()?.overlay()?.toggleOverlay(false);
  }
}
